import {
  type AnyEvent,
  type Flags,
  FlagsSchema,
  mergeFlags,
  SYNC_PUSH_MAX,
  SyncPullResponse,
  SyncPushResponse,
} from '@disdis/shared'
import { log, reason } from '../app/log.js'
import { getMeta, setMeta } from './db.js'
import { insertPulled, markSynced, unsyncedEvents } from './events.js'

/**
 * ARCHITECTURE §5. Events only, in both directions. Sync failures are silent in the child UI
 * and surface in the parent area, so every call returns a result instead of throwing.
 */

const logger = log('sync')

let apiBase: string = import.meta.env.VITE_API_BASE ?? '/api'
let authHeader: string | null = null

export function apiBaseUrl(): string {
  return apiBase
}

/** Tests point the client at a local API; the Capacitor build points it at the box. */
export function setApiBase(base: string): void {
  apiBase = base
}

/** Capacitor clients send a bearer token; the web build uses the httpOnly cookie. */
export function setAuthHeader(header: string | null): void {
  authHeader = header
}

export type SyncStatus = 'synced' | 'pending' | 'error'
export type SyncResult = { status: SyncStatus; pushed: number; pulled: number; detail?: string }

async function request(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(authHeader ? { authorization: authHeader } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  })
}

export async function pushEvents(childId: string): Promise<{ pushed: number; more: boolean }> {
  const events = await unsyncedEvents(childId, SYNC_PUSH_MAX)
  if (events.length === 0) return { pushed: 0, more: false }

  const response = await request('/sync/push', {
    method: 'POST',
    body: JSON.stringify({ childId, events }),
  })
  if (!response.ok) throw new Error(`push failed with ${response.status}`)

  const body = SyncPushResponse.parse(await response.json())
  await markSynced(body.accepted)
  for (const row of body.rejected) logger.warn(`the server rejected ${row.id}`, row.reason)
  return { pushed: body.accepted.length, more: events.length === SYNC_PUSH_MAX }
}

export async function pullEvents(childId: string): Promise<number> {
  const key = `lastSyncSeq:${childId}`
  let sinceSeq = (await getMeta<number>(key)) ?? 0
  let pulled = 0

  for (let page = 0; page < 20; page++) {
    const response = await request(
      `/sync/pull?childId=${encodeURIComponent(childId)}&sinceSeq=${sinceSeq}`,
    )
    if (!response.ok) throw new Error(`pull failed with ${response.status}`)
    const body = SyncPullResponse.parse(await response.json())
    pulled += await insertPulled(body.events as AnyEvent[])
    sinceSeq = Math.max(sinceSeq, body.latestSeq)
    await setMeta(key, sinceSeq)
    if (body.nextSince === null) break
    sinceSeq = body.nextSince
  }
  return pulled
}

/** Push then pull, the order ARCHITECTURE §5 asks for on append, foreground and boot. */
export async function syncNow(childId: string): Promise<SyncResult> {
  try {
    let pushed = 0
    let more = true
    for (let batch = 0; batch < 10 && more; batch++) {
      const result = await pushEvents(childId)
      pushed += result.pushed
      more = result.more
    }
    const pulled = await pullEvents(childId)
    return { status: 'synced', pushed, pulled }
  } catch (error) {
    logger.warn('sync did not complete', reason(error))
    return { status: 'error', pushed: 0, pulled: 0, detail: reason(error) }
  }
}

export async function fetchFlags(): Promise<Flags> {
  try {
    const response = await request('/flags')
    if (!response.ok) throw new Error(`flags failed with ${response.status}`)
    return FlagsSchema.parse(await response.json())
  } catch (error) {
    logger.warn('using the built in flags', reason(error))
    return mergeFlags(await getMeta<Partial<Flags>>('featureFlags'))
  }
}

/**
 * Debounced push, 2 s per ARCHITECTURE §5. One timer per child so a burst of appends is one
 * request.
 */
const timers = new Map<string, ReturnType<typeof setTimeout>>()

export function scheduleSync(childId: string, onResult?: (result: SyncResult) => void): void {
  const existing = timers.get(childId)
  if (existing) clearTimeout(existing)
  timers.set(
    childId,
    setTimeout(() => {
      timers.delete(childId)
      void syncNow(childId).then((result) => onResult?.(result))
    }, 2000),
  )
}
